import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LogOut } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { LanguageSelector } from "@/components/LanguageSelector";
import { useSession, signOut } from "@/lib/auth-client";

export default function Profile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const user = session?.user as {
    name?: string;
    email?: string;
    role?: string;
  } | null;

  const initials = user?.name
    ? user.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "?";

  const handleLogout = async () => {
    await signOut();
    navigate("/login");
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto space-y-4">
        <h1 className="text-2xl font-bold">{t("profile.title")}</h1>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xl font-bold shrink-0">
                {initials}
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-lg truncate">{user?.name}</p>
                <p className="text-sm text-muted-foreground truncate">
                  {user?.email}
                </p>
                <span className="text-xs bg-secondary px-2 py-0.5 rounded-full mt-1 inline-block capitalize">
                  {user?.role}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6 space-y-3">
            <p className="text-sm font-medium">{t("profile.language")}</p>
            <LanguageSelector />
          </CardContent>
        </Card>

        <Button variant="destructive" className="w-full" onClick={handleLogout}>
          <LogOut className="h-4 w-4 mr-2" />
          {t("common.logout")}
        </Button>

        <p className="text-center text-xs text-muted-foreground">
          {import.meta.env.VITE_APP_VERSION ?? "dev"}
        </p>
      </div>
    </Layout>
  );
}
